import { LIST_URL_SPECS } from '@pmp/utils';

import type { AppliedCrudDraft, CrudDraftInstruction, DraftController, ReadDraftController } from './draftActions';
import {
  applyCrudDraft,
  describeDraftApplied,
  describeDraftOffer,
  holdCrudDraft,
  offerOpenFormCall,
  takeOpenFormOffer,
  OPEN_CREATE_FORM,
  OPEN_CREATE_FORM_PARAMETERS,
} from './draftActions';
import { assistantNavigate } from './listRegistry';
import type { AssistantAction } from '@ohpen/enso-ui';

import { isSafePath, pathname } from './proposalChannel';

export { clearPendingCrudDraft, drainCrudDraft, holdCrudDraft, takeCrudDraft } from './draftActions';

// Which create forms are reachable right now, so the agent's proposal reaches the page the user can act on.
//
// Not a store, for the same reason the list controllers are not one: nothing renders from this, and the
// transport reads it outside React. Each page registers its own create form while it is mounted and clears it
// on the way out, and a proposal for a page that is not mounted waits in `draftActions` until it comes up.

/**
 * What became of a proposal. Told apart because the panel says them to the user in different words: a page
 * that is not open is not the same as one that is open and has none of the fields the agent named.
 */
export type CrudDraftOutcome =
  | { status: 'opened'; applied: AppliedCrudDraft }
  | { status: 'navigating' }
  | { status: 'needs-confirmation' }
  | { status: 'unknown-fields'; dropped: string[] }
  | { status: 'not-open' };

const controllers = new Map<string, ReadDraftController>();

export const registerDraftController = (entityType: string, read: ReadDraftController): void => {
  controllers.set(entityType, read);
};

export const unregisterDraftController = (entityType: string, read: ReadDraftController): void => {
  // Only when it is still this page's own reader: on a route change the next page can register before the
  // previous one unmounts, and clearing then would leave the app with no reachable form.
  if (controllers.get(entityType) === read) controllers.delete(entityType);
};

export const findDraftController = (entityType: string): DraftController | undefined => controllers.get(entityType)?.() ?? undefined;

// The path the agent sent, only when it is this app's own path for the page the proposal names. The registry,
// not the proposal, decides where a form lives.
const navigablePath = (instruction: CrudDraftInstruction): string | null => {
  const spec = LIST_URL_SPECS[instruction.entity];
  const path = instruction.path;
  if (spec === undefined || path === undefined || !isSafePath(path)) return null;

  return pathname(path) === spec.route ? path : null;
};

/**
 * Opens the create form the proposal names, filled in: straight away when its page is on screen, and
 * otherwise by navigating there and letting the page open it as it mounts.
 *
 * The navigation half only ever runs for a proposal the user has agreed to. Nothing is submitted either way,
 * but taking someone off the page they are working on is not a side effect of answering their question, so a
 * proposal that would move them and does not say they agreed is refused here rather than obeyed.
 *
 * Says which of the five things happened, so the step the panel paints is the one the user can check against
 * their own screen.
 */
export const runCrudDraft = (instruction: CrudDraftInstruction): CrudDraftOutcome => {
  const controller = findDraftController(instruction.entity);
  if (controller !== undefined) {
    const applied = applyCrudDraft(instruction, controller);
    return applied === null
      ? { status: 'unknown-fields', dropped: Object.keys(instruction.values ?? {}) }
      : { status: 'opened', applied };
  }

  const path = navigablePath(instruction);
  if (path === null) return { status: 'not-open' };
  if (instruction.openForm !== true) return { status: 'needs-confirmation' };

  holdCrudDraft(instruction);
  if (!assistantNavigate(path)) return { status: 'not-open' };

  return { status: 'navigating' };
};

/**
 * The action behind the panel's confirm affordance: the user agreeing to be taken to a form they do not have
 * open, filled in with what the assistant proposed. Registered on the assistant config, so enso offers it on
 * the step itself and runs it when they click.
 *
 * `confirm` is enso's default, and is set here anyway because the whole point of this action is that it is
 * never taken without being agreed to.
 */
export const openCreateFormAction: AssistantAction = {
  name: OPEN_CREATE_FORM,
  description:
    'Open one of the portal create forms, filled in with the values the answer proposes. Offered to the user, never taken on its own. It submits nothing.',
  parameters: OPEN_CREATE_FORM_PARAMETERS,
  confirm: 'confirm',
  execute: params => {
    const instruction = takeOpenFormOffer(params);
    if (instruction === null) return 'That proposal is no longer available.';

    const outcome = runCrudDraft(instruction);
    switch (outcome.status) {
      case 'opened':
        return describeDraftApplied(outcome.applied, instruction.form);
      case 'navigating':
        return `Opening the ${instruction.form ?? instruction.entity} form`;
      case 'unknown-fields':
        return `The ${instruction.form ?? instruction.entity} form has no ${outcome.dropped.join(', ')}, so nothing was filled in`;
      default:
        return `The ${instruction.form ?? instruction.entity} form could not be opened`;
    }
  },
  serializeResult: result => String(result),
};

/** The offer the panel paints when the page is elsewhere: the call, and the words on the step. */
export const offerCreateForm = (instruction: CrudDraftInstruction): { arguments: Record<string, unknown>; summary: string } => ({
  arguments: offerOpenFormCall(instruction),
  summary: describeDraftOffer(instruction),
});
